import {
  CheckIcon,
  ChevronsUpDownIcon,
  CopyIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  SaveIcon,
  TrashIcon,
} from 'lucide-react';
import * as React from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTitle,
} from '@nucleus-commerce/ui/components/popover';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import type { DataTableView } from './types';
import type { UseDataTableViewsResult } from './use-data-table-views';

/** Shopify's limit on view names. */
const MAX_VIEW_NAME = 40;

export interface DataTableViewNameFormProps {
  title: string;
  submitLabel: string;
  initialName?: string;
  isAvailable: (name: string) => boolean;
  onSubmit: (name: string) => void;
  onCancel: () => void;
}

/** Name a view: trimmed, at most 40 characters, unique case-insensitively. */
function DataTableViewNameForm({
  title,
  submitLabel,
  initialName = '',
  isAvailable,
  onSubmit,
  onCancel,
}: DataTableViewNameFormProps) {
  const { t } = useTranslation('common');
  const id = React.useId();
  const [name, setName] = React.useState(initialName);
  const [touched, setTouched] = React.useState(false);
  const trimmed = name.trim();
  const unchanged = trimmed === initialName.trim() && initialName !== '';
  const error =
    trimmed === ''
      ? t('indexTable.views.nameRequired')
      : !unchanged && !isAvailable(trimmed)
        ? t('indexTable.views.nameTaken')
        : null;

  return (
    <form
      data-slot="data-table-view-name-form"
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        setTouched(true);
        if (error) return;
        if (!unchanged) onSubmit(trimmed);
        else onCancel();
      }}
    >
      <PopoverTitle>{title}</PopoverTitle>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={id} className="text-sm font-medium">
          {t('indexTable.views.name')}
        </label>
        <Input
          id={id}
          autoFocus
          value={name}
          maxLength={MAX_VIEW_NAME}
          aria-invalid={touched && error ? true : undefined}
          aria-describedby={touched && error ? `${id}-error` : `${id}-count`}
          onChange={(event) => setName(event.target.value)}
        />
        {touched && error ? (
          <p id={`${id}-error`} className="text-destructive text-sm">
            {error}
          </p>
        ) : (
          <p id={`${id}-count`} className="text-muted-foreground text-xs">
            {name.length}/{MAX_VIEW_NAME}
          </p>
        )}
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('actions.cancel')}
        </Button>
        <Button type="submit" size="sm">
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

type Dialog = 'save' | 'rename' | 'duplicate' | 'delete' | null;

export interface DataTableViewMenuProps {
  views: UseDataTableViewsResult;
}

/**
 * The pill before the search field ("All ⇅"), always there, as in the Shopify admin. A view's
 * filters are ordinary filters, so they also show as chips in the field, and this pill follows
 * them. Built-in views come first, saved ones after; the view shown has a "..." menu, and the
 * query on screen can be saved from the bottom of the list.
 */
function DataTableViewMenu({ views }: DataTableViewMenuProps) {
  const { t } = useTranslation('common');
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [dialog, setDialog] = React.useState<Dialog>(null);
  // Menu items open their form after the menu has closed and returned focus,
  // or the focus return would count as an outside click and close it.
  const dialogAfterMenu = React.useRef<Dialog>(null);
  const selected = views.selected;
  if (!selected) return null;

  const all = [...views.views, ...views.savedViews];
  const select = (id: string) => {
    const view = all.find((v) => v.id === id);
    if (view) views.select(view);
  };
  const close = () => setDialog(null);

  const openAfterMenu = (next: Dialog) => {
    dialogAfterMenu.current = next;
  };

  return (
    <Popover
      open={dialog !== null}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DropdownMenu>
        <PopoverAnchor asChild>
          <DropdownMenuTrigger asChild>
            <Button
              ref={triggerRef}
              variant="secondary"
              size="sm"
              className="h-8 shrink-0 gap-1.5 pr-2 pl-2.5 font-semibold"
              aria-label={t(
                views.isModified ? 'indexTable.views.triggerEdited' : 'indexTable.views.trigger',
                { view: selected.label },
              )}
            >
              <span className="max-w-40 truncate">{selected.label}</span>
              <ChevronsUpDownIcon className="text-muted-foreground size-3.5" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
        </PopoverAnchor>
        <DropdownMenuContent
          align="start"
          className="max-w-72 min-w-52"
          onCloseAutoFocus={(event) => {
            const next = dialogAfterMenu.current;
            if (next === null) return;
            dialogAfterMenu.current = null;
            event.preventDefault();
            setDialog(next);
          }}
        >
          {views.views.map((view) => (
            <ViewItem
              key={view.id}
              view={view}
              shown={view.id === selected.id}
              saved={false}
              canCopy={views.canSave}
              onSelect={select}
              onManage={openAfterMenu}
            />
          ))}
          {views.savedViews.length > 0 ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>{t('indexTable.views.saved')}</DropdownMenuLabel>
              {views.savedViews.map((view) => (
                <ViewItem
                  key={view.id}
                  view={view}
                  shown={view.id === selected.id}
                  saved
                  canCopy={views.canSave}
                  onSelect={select}
                  onManage={openAfterMenu}
                />
              ))}
            </>
          ) : null}
          {views.canSave ? (
            <>
              <DropdownMenuSeparator />
              {views.selectedIsSaved && views.isModified ? (
                <DropdownMenuItem onClick={views.saveSelected}>
                  <SaveIcon aria-hidden />
                  {t('indexTable.views.update')}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem disabled={!views.isModified} onClick={() => openAfterMenu('save')}>
                <PlusIcon aria-hidden />
                {t('indexTable.views.saveAsNew')}
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <PopoverContent
        align="start"
        className="w-80"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          triggerRef.current?.focus();
        }}
      >
        {dialog === 'save' ? (
          <DataTableViewNameForm
            title={t('indexTable.views.saveAsTitle')}
            submitLabel={t('indexTable.views.save')}
            isAvailable={(name) => views.isLabelAvailable(name)}
            onSubmit={(name) => {
              views.saveAs(name);
              close();
            }}
            onCancel={close}
          />
        ) : dialog === 'rename' ? (
          <DataTableViewNameForm
            title={t('indexTable.views.rename')}
            submitLabel={t('indexTable.views.save')}
            initialName={selected.label}
            isAvailable={(name) => views.isLabelAvailable(name, selected.id)}
            onSubmit={(name) => {
              views.rename(selected.id, name);
              close();
            }}
            onCancel={close}
          />
        ) : dialog === 'duplicate' ? (
          <DataTableViewNameForm
            title={t('indexTable.views.duplicate')}
            submitLabel={t('indexTable.views.create')}
            initialName={t('indexTable.views.copyOf', { view: selected.label }).slice(
              0,
              MAX_VIEW_NAME,
            )}
            isAvailable={(name) => views.isLabelAvailable(name)}
            onSubmit={(name) => {
              const copy = views.duplicate(selected.id, name);
              if (copy) views.select(copy);
              close();
            }}
            onCancel={close}
          />
        ) : dialog === 'delete' ? (
          <div className="flex flex-col gap-3">
            <PopoverTitle>{t('indexTable.views.deleteTitle')}</PopoverTitle>
            <p className="text-muted-foreground text-sm">
              {t('indexTable.views.deleteDescription', { view: selected.label })}
            </p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={close}>
                {t('actions.cancel')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                autoFocus
                onClick={() => {
                  views.remove(selected.id);
                  close();
                }}
              >
                {t('indexTable.views.delete')}
              </Button>
            </div>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

interface ViewItemProps {
  view: DataTableView;
  shown: boolean;
  saved: boolean;
  /** A copy is a saved view, so only a table that can keep them offers it. */
  canCopy: boolean;
  onSelect: (id: string) => void;
  onManage: (dialog: Dialog) => void;
}

/** One row: a check on the view shown, which also gets "..." for its rename, copy and delete. */
function ViewItem({ view, shown, saved, canCopy, onSelect, onManage }: ViewItemProps) {
  const { t } = useTranslation('common');
  return (
    <>
      {view.separatorBefore ? <DropdownMenuSeparator /> : null}
      <div className="flex items-center gap-0.5">
        <DropdownMenuItem
          role="menuitemradio"
          aria-checked={shown}
          className={cn('min-w-0 flex-1', shown && 'font-semibold')}
          onClick={() => onSelect(view.id)}
        >
          <CheckIcon aria-hidden className={cn('size-3.5', !shown && 'invisible')} />
          <span className="flex-1 truncate">{view.label}</span>
        </DropdownMenuItem>
        {shown && (saved || canCopy) ? (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              aria-label={t('indexTable.views.actionsFor', { view: view.label })}
              className="shrink-0 px-1.5 [&>svg:last-child]:hidden"
            >
              <MoreHorizontalIcon aria-hidden className="size-4" />
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {saved ? (
                <DropdownMenuItem onClick={() => onManage('rename')}>
                  <PencilIcon aria-hidden />
                  {t('indexTable.views.rename')}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onClick={() => onManage('duplicate')}>
                <CopyIcon aria-hidden />
                {t('indexTable.views.duplicate')}
              </DropdownMenuItem>
              {saved ? (
                <DropdownMenuItem variant="destructive" onClick={() => onManage('delete')}>
                  <TrashIcon aria-hidden />
                  {t('indexTable.views.delete')}
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ) : null}
      </div>
    </>
  );
}

export { DataTableViewMenu, DataTableViewNameForm, MAX_VIEW_NAME };
